import { v4 as uuid } from 'uuid';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { cookie } from '@turnercode/cdp-cookie';
import { FeatureFlagClient, IQueryFeatureResult } from '@turnercode/cdp-feature-flag-client';
import { Queue } from '@turnercode/cdp-queue';
import { initIdentity, WmukidValue, CdpidValue } from './identity';
import { PayloadCore, payloadCore, SessionProperties } from './payloadCore';
// import { initPrivacy, USPData, uspString } from './privacy';
import {
  URLS,
  MAX_SESSION_DURATION,
  featureFlagLocalConfig,
  featureFlagDefaults,
  queueOptions,
} from './utils/constants';
import { getAttUuid, getConvivaId, getEcid, getKruxId, getZionId, hydratePayload } from './utils/hydratePayload';
import { processQueue } from './utils/processQueue';
import { sendRequest } from './utils/sendRequest';
import { createLogger, error, warn, info } from './utils/logger';
import pkg from '../package.json';

export interface PsmConfig {
  platform?: string;
  brand: string;
  subBrand?: string;
  psmEnvironment: string;
  cookieDomain?: string;
  cookieSameSite?: string;
  cookieSecure?: boolean;
  cookieExpires?: number;
  countryCode?: string;
}

export interface Geolocation {
  asn: {
    id: string;
    name: string;
  };
  continent: string;
  continentName: string;
  country: string;
  country_alpha2: string;
  country_alpha3: string;
  ip_address: string;
  lat?: string;
  lon?: string;
  proxy: null | string;
  states: {
    cities: string[];
    counties: string[];
    state: string;
    zipcodes: string[];
  }[];
}

export interface IdResolveResponse {
  wmhhid?: string;
  wminid?: string;
  wmResolvedUserData: { [key: string]: any };
}

const win = window as any;
const doc = win.document as any;

export class Psm {
  /**
   * Flag set to true when Prism has initialized
   */
  ready = false;

  /**
   * Default values from Prism config
   */
  config: PsmConfig = {
    platform: 'web',
    brand: '',
    subBrand: '',
    psmEnvironment: undefined,
    cookieSameSite: 'Lax',
    cookieSecure: false,
    cookieExpires: 31536000000, // 1 year in miliseconds
  };

  /**
   * Feature flag client instance
   */
  ffClient: FeatureFlagClient;

  /**
   * Feature flags
   */
  flags: IQueryFeatureResult[] = [];

  /**
   * Request queue for Doppler privacy API
   */
  privacyQueue: Queue;

  /**
   * Request queue for Doppler identity API
   */
  identityQueue: Queue;

  /**
   * Request queue for Doppler telemetry API
   */
  telemetryQueue: Queue;

  /**
   * Geolocation API response
   */
  location: Partial<Geolocation> = {
    asn: {
      id: '',
      name: '',
    },
    continent: '',
    continentName: '',
    country: '',
    country_alpha2: '',
    country_alpha3: '',
    ip_address: '',
    lat: '',
    lon: '',
    proxy: null,
    states: [
      {
        cities: [],
        counties: [],
        state: '',
        zipcodes: [],
      },
    ],
  };

  /**
   * WMUKID from cookie or generated when Prism is initialized
   */
  wmukid: WmukidValue = null;

  /**
   * CDPID from cookie or generated when Prism is initialized
   */
  cdpid: CdpidValue = null;

  /**
   * Country code value for location-guarded logic
   */
  countryCode: string;

  /**
   * Session properties as determine on initialization
   */
  session: SessionProperties;

  /**
   * User's browser -- currently only sets to Safari or Unknown
   */
  browser: string;

  constructor() {
    win.WM = win.WM || {};
    win.WM.PSM = win.WM.PSM || {};
    win.WM.PSM.initPsm = this.initPsm.bind(this);
    win.WM.PSM.getVersion = this.getVersion.bind(this);
    win.WM.PSM.getWMUKID = this.getWMUKID.bind(this);
    win.WM.PSM.getCDPID = this.getCDPID.bind(this);
    win.WM.PSM.getConfig = this.getConfig.bind(this);
    win.WM.PSM.getFlags = this.getFlags.bind(this);
    win.WM.PSM.isDopplerIdentityOnStartEnabled = () => this.queryFlag('doppler-identity-onstart');
    win.WM.PSM.isDopplerIdentityOnCompleteEnabled = () => this.queryFlag('doppler-identity-oncomplete');
    win.WM.PSM.isDopplerPrivacyEnabled = () => this.queryFlag('doppler-privacy');
    win.WM.PSM.isDopplerTelemetryEnabled = () => this.queryFlag('doppler-telemetry');
  }

  async initPsm(config: PsmConfig): Promise<void> {
    if (this.ready) return;
    this.config = Object.assign(this.config, config);

    createLogger(this);

    if (typeof config === 'undefined') {
      const err = new Error('[PSM]: Please provide a valid configuration to initPsm');
      error({
        err,
        eventType: 'configValidation',
        methodName: 'initPsm',
      });
      throw err;
    }

    const init = () => {
      const env = config.psmEnvironment.toUpperCase();
      const core: PayloadCore = payloadCore();

      // this.privacyQueue = new Queue('privacy', queueOptions, processQueue(URLS.privacy[env]));
      this.identityQueue = new Queue('identity', queueOptions, processQueue(URLS.identity[env]));
      // this.telemetryQueue = new Queue('telemetry', queueOptions, processQueue(URLS.telemetry[env]));

      // initPrivacy(config.enablePrivacy, this.countryCode);
      const ids = initIdentity(env, this.countryCode);

      this.wmukid = ids.wmukid;
      this.cdpid = ids.cdpid;

      // Only set session properties if WMUKID can be set
      this.wmukid !== 'Unknown' && this.setSessionProperties();

      // Only resolve IDs if WMUKID can be set
      this.wmukid !== 'Unknown' && this.resolveIds();

      // Only set sendHHID cookie if WMUKID can be set
      this.wmukid !== 'Unknown' && this.setSendHHIDCookie();

      hydratePayload(this, core);

      // Publish to ZionMessageBus
      if (win.ZionMessageBus) {
        win.ZionMessageBus.getInstance().publish('id_found', { type: 'wmukid', value: this.wmukid });
      }

      core.trackIdentityRegistration('identity on page start', new Date().toISOString(), (data) => {
        if (this.queryFlag('doppler-identity-onstart')) {
          this.identityQueue.addItem(data);
          this.resetNewSessionFields();
        }

        if (env === 'AUTOMATED_TEST') {
          win.localStorage.setItem('payload-on-start', JSON.stringify(data));
        }
      });
      win.addEventListener('load', () => {
        hydratePayload(this, core);
        core.trackIdentityRegistration('identity on page complete', new Date().toISOString(), (data) => {
          if (this.queryFlag('doppler-identity-oncomplete')) {
            this.identityQueue.addItem(data);

            if (env === 'AUTOMATED_TEST') {
              win.localStorage.setItem('payload-on-complete', JSON.stringify(data));
            }
            this.resetNewSessionFields();
          }
        });
      });
      doc.addEventListener('userConsentChanged', () => {
        console.log('[PSM]: Consent change event registered');
        hydratePayload(this, core);
        core.trackConsentUpdated('userConsentChanged', new Date().toISOString(), (data) => {
          if (this.queryFlag('doppler-consent-update')) {
            console.log('[PSM]: consent updated', data);
            this.identityQueue.addItem(data);
            this.resetNewSessionFields();
          }
        });
      });

      // this.privacyQueue.start();
      this.identityQueue.start();
      // this.telemetryQueue.start();

      this.ready = true;
    };

    this.validateConfig(config);

    cookie.options({
      domain: this.config.cookieDomain,
      maxage: this.config.cookieExpires,
      path: '/',
      samesite: this.config.cookieSameSite,
      secure: this.config.cookieSecure,
    });

    this.browser = this.setBrowser(window.navigator.userAgent);

    this.ffClient = new FeatureFlagClient(
      {
        configUrl: URLS.featureFlag[config.psmEnvironment.toUpperCase()],
        configRefreshInterval: 60000,
        userTargetingProperties: {
          Platform: ['Web'],
          Brand: [config.brand],
          Browser: this.browser,
        },
        clientId: `psmFFClient-${this.config.brand}-${this.config.subBrand}`,
      },
      featureFlagLocalConfig,
      true
    );

    try {
      this.location = await sendRequest(URLS.locate);
    } catch (err) {
      error({
        err,
        methodName: 'sendRequest',
        eventType: 'geolocation',
      });
      this.location.country = undefined;
      this.location.country_alpha2 = undefined;
    }
    this.countryCode =
      this.hasProperty('countryCode', config) &&
      typeof config.countryCode !== 'undefined' &&
      this.config.countryCode.length
        ? this.config.countryCode.toUpperCase()
        : this.location.country_alpha2;
    try {
      const flags = await this.ffClient.queryAllFeatureFlags();
      this.flags = flags.results;
      init();
    } catch (err) {
      error({
        err,
        methodName: 'queryAllFeatureFlags',
        eventType: 'featureFlagging',
      });
      init();
    }
  }

  setBrowser(userAgent): string {
    // Per MDN, Safari UserAgent both must contain `Safari` and must not contain `Chrome` or `Chromium`
    const isSafari =
      userAgent.indexOf('Safari') > -1 &&
      userAgent.indexOf('Chrome') === -1 &&
      userAgent.indexOf('Chromium') === -1 &&
      userAgent.indexOf('CriOS') === -1 &&
      userAgent.indexOf('EdgiOS') === -1 &&
      userAgent.indexOf('FxiOS') === -1;

    this.browser = isSafari ? 'safari' : 'unknown';
    return this.browser;
  }

  setSendHHIDCookie() {
    const sendHHID = this.queryFlag('sendHHID');
    return cookie.set('sendHHID', sendHHID);
  }

  async resolveIds() {
    // Confirm IDR service is enabled
    if (!this.queryFlag('idresolve')) {
      info({
        message: '[PSM]: idresolve flag is disabled',
        methodName: 'resolveIds',
        eventType: 'idresolution',
      });
      return false;
    }

    // Check if previous IDR request happened within 24 hours
    const idrTimestampCookie = cookie.get('idrTimestamp') as string | null;
    if (idrTimestampCookie) {
      const idrTimestamp = new Date(idrTimestampCookie);
      const currentTimestamp = new Date();
      const idrLifespan = (currentTimestamp.getTime() - idrTimestamp.getTime()) / 1000 / 60 / 60; // Get lifespan in hours

      if (idrLifespan < 24) {
        info({
          message: `[PSM]: IDR lifespan, ${idrLifespan}, less than 24 hours`,
          methodName: 'resolveIds',
          eventType: 'idresolution',
        });
        return false;
      }
    }
    const idrRequestBody = {
      wmukid: this.getWMUKID(),
      // TODO: Replace with abstracted `getThirdPartyIds` method when it exists
      ids: {
        attuuid: getAttUuid(),
        cdpid: this.getCDPID(),
        kruxid: getKruxId(),
        ecid: getEcid(),
        convivaid: getConvivaId(),
        zionid: getZionId(),
        wmhhid: cookie.get('wmhhid'),
        wminid: cookie.get('wminid'),
      },
    };

    // Make IDR request
    try {
      const idrResponse: IdResolveResponse = await sendRequest(URLS.idresolve[this.config.psmEnvironment], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(idrRequestBody),
      });
      const { wmhhid, wminid } = idrResponse;
      wmhhid && cookie.set('wmhhid', wmhhid);
      wminid && cookie.set('wminid', wminid);
    } catch (err) {
      error({
        err,
        eventType: 'idresolution',
        methodName: 'sendRequest',
      });
    }
    // Set IDR timestamp regardless of IDR response
    cookie.set('idrTimestamp', new Date());

    return true;
  }

  setSessionProperties() {
    const currentTimestamp = new Date();
    let sessionProperties: SessionProperties = {
      sessionid: uuid(),
      sessionDuration: 0,
      psmLastActiveTimestamp: currentTimestamp.toISOString(),
      psmSessionStart: currentTimestamp.toISOString(),
      isSessionStart: true,
    };

    // Get all stored session info
    const prevSessionId = cookie.get('psmSessionId') as string | null;
    const prevSessionStartCookie = cookie.get('psmSessionStart') as string | null;
    const prevLastActiveTimestampCookie = cookie.get('psmLastActiveTimestamp') as string | null;

    if (prevSessionId !== null) {
      const prevLastActiveTimestamp = new Date(prevLastActiveTimestampCookie);
      const prevSessionStartTimestamp = new Date(prevSessionStartCookie);
      const timeSinceLastActivity = currentTimestamp.getTime() - prevLastActiveTimestamp.getTime(); // time past in miliseconds

      // if session >max time, then create new session
      if (timeSinceLastActivity > MAX_SESSION_DURATION) {
        info({
          message: `[PSM]: Session ${prevSessionId} timed out after ${timeSinceLastActivity / 1000} seconds.`,
          methodName: 'setSessionProperties',
          eventType: 'session',
        });
        sessionProperties = {
          ...sessionProperties,
          previousSession: {
            sessionid: prevSessionId,
            sessionDuration: (prevLastActiveTimestamp.getTime() - prevSessionStartTimestamp.getTime()) / 1000,
            psmLastActiveTimestamp: prevLastActiveTimestampCookie,
            psmSessionStart: prevSessionStartCookie,
          },
        };
        // If session <max time, update psmLastActive timestamp
      } else {
        info({
          message: `[PSM]: Session ${prevSessionId} still active after ${
            timeSinceLastActivity / 1000
          } seconds. Updating last active timestamp.`,
          methodName: 'setSessionProperties',
          eventType: 'session',
        });
        sessionProperties = {
          sessionid: prevSessionId,
          sessionDuration: (currentTimestamp.getTime() - prevSessionStartTimestamp.getTime()) / 1000,
          psmLastActiveTimestamp: currentTimestamp.toISOString(),
          psmSessionStart: prevSessionStartCookie,
          isSessionStart: false,
        };
      }
    } else {
      info({
        message: `[PSM]: Creating new session`,
        methodName: 'setSessionProperties',
        eventType: 'session',
      });
    }

    // Set all session cookie values
    cookie.set('psmSessionId', sessionProperties.sessionid);
    cookie.set('psmLastActiveTimestamp', sessionProperties.psmLastActiveTimestamp);
    cookie.set('psmSessionStart', sessionProperties.psmSessionStart);

    this.session = sessionProperties;
    return;
  }

  // Clears isSessionStart/previousSession properties
  // Called after each queue addition so it's only cleared after sending to Doppler
  resetNewSessionFields() {
    this.session = {
      ...this.session,
      isSessionStart: false,
      previousSession: null,
    };
  }

  getVersion() {
    return pkg.version;
  }

  getWMUKID() {
    return (cookie.get('WMUKID_STABLE') as string) || 'Unknown';
  }

  getCDPID() {
    const data = cookie.get('CDPID') as CdpidValue;

    if (data && typeof data === 'object' && 'cdpId' in data && 'wmukId' in data) {
      return data.cdpId;
    }
    return (data as string) || 'Unknown';
  }

  getConfig() {
    return this.config;
  }

  getFlags() {
    return this.flags;
  }

  queryFlag(flagId: string): boolean {
    let flagEnabled: boolean = featureFlagDefaults[flagId] || false;
    try {
      const { enabled } = this.flags.find((flag) => {
        return flag.flagId === flagId;
      });

      flagEnabled = enabled;
    } catch (err) {
      warn({
        err,
        methodName: 'queryFlag',
        eventType: 'featureFlagging',
      });
    }

    return flagEnabled;
  }

  private validateConfig(config: Partial<PsmConfig>) {
    const errors: string[] = [];

    if (!this.hasProperty('psmEnvironment', config)) errors.push('Please specify your psmEnvironment.');
    if (!this.hasProperty('brand', config)) errors.push('Please specify your brand.');

    if (errors.length) {
      const err = new Error(['[PSM]: Invalid configuration provided.'].concat(errors).join('\n'));

      error({
        err,
        methodName: 'validateConfig',
        eventType: 'configValidation',
      });
      throw err;
    }
  }

  private hasProperty(prop: string, obj: any) {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  }
}
